// for tests
/* eslint-disable:next-line variable-name */
import Vue from 'vue';
import ISelection from '@core/models/betSlip/ISelection';
import { BetSlipType } from '@core/models/shared/betSlip/betSlipEnums';
import { getConfig } from '@superbet-group/web.lib.config';
import { BetSlipPurchaseType } from '@superbet-group/web.types';

import { Payout } from '@core/models/betSlip/Payout';
import SelectionChange from '@core/models/betSlip/SelectionChange';
import IBetSlipError from '@core/models/betSlip/validation/IBetSlipError';

import { BetSlipState } from './types';

export enum MUTATION_TYPE {
    SET_AUTO_UPDATE_CHANGES = 'SET_AUTO_UPDATE_CHANGES',
    ADD_SELECTION_CHANGE = 'ADD_SELECTION_CHANGE',
    REMOVE_SELECTION_CHANGE = 'REMOVE_SELECTION_CHANGE',
    CLEAR_SELECTION_CHANGES = 'CLEAR_SELECTION_CHANGES',
    ADD_ERROR = 'ADD_ERROR',
    SET_SELECTION_ADD_ERROR = 'SET_SELECTION_ADD_ERROR',
    CLEAR_ERRORS = 'CLEAR_ERRORS',
    SET_SELECTION_ERRORS = 'SET_SELECTION_ERRORS',
    SET_PURCHASE_TYPE = 'SET_PURCHASE_TYPE',
    SET_PAYOUT = 'SET_PAYOUT',
    SET_PAYOUT_IS_LOCKED = 'SET_PAYOUT_IS_LOCKED',
    SET_WARNING = 'SET_WARNING',
    SET_CHANGES = 'SET_CHANGES',
    SET_BONUS_ELIGIBLITY_ERROR = 'SET_BONUS_ELIGIBLITY_ERROR',
    CLEAR_BONUS_ELIGIBLITY_ERROR = 'CLEAR_BONUS_ELIGIBLITY_ERROR',
    SET_BONUS_WARNING = 'SET_BONUS_WARNING',
    SET_IS_BONUS_SELECTED = 'SET_IS_BONUS_SELECTED',
    SET_IS_RECREATING_TICKET = 'SET_IS_RECREATING_TICKET',
    SET_IS_SUBMITTING_ODD = 'SET_IS_SUBMITTING_ODD',
    SET_STAKE = 'SET_STAKE',
    ADD_SELECTION = 'ADD_SELECTION',
    UPDATE_SELECTION = 'UPDATE_SELECTION',
    REMOVE_SELECTION = 'REMOVE_SELECTION',
    CLEAR_SELECTIONS = 'CLEAR_SELECTIONS',
    TOGGLE_SELECTION_IS_FIXED = 'TOGGLE_SELECTION_IS_FIXED',
    SET_TYPE = 'SET_TYPE',
    ADD_SELECTED_SYSTEM = 'SET_SELECTED_SYSTEM',
    REMOVE_SELECTED_SYSTEM = 'REMOVE_SELECTED_SYSTEM',
    CLEAR_SELECTED_SYSTEMS = 'CLEAR_SELECTED_SYSTEMS',
    SET_SELECTIONS = 'SET_SELECTIONS',
    SET_SELECTED_SYSTEMS = 'SET_SELECTED_SYSTEMS',
    SET_SELECTION_IS_FIXED = 'SET_SELECTION_IS_FIXED',
    SET_BETSLIP_ERRORS = 'SET_BETSLIP_ERRORS',
    CLEAR_BETSLIP_ERRORS = 'CLEAR_BETSLIP_ERRORS',
}

export const mutations: Record<MUTATION_TYPE, (state: BetSlipState, param: any) => void> = {
    [MUTATION_TYPE.SET_AUTO_UPDATE_CHANGES](state, value: boolean) {
        state.autoUpdateChanges = value;
    },
    [MUTATION_TYPE.ADD_SELECTION_CHANGE](state, change: SelectionChange) {
        Vue.set(state.changes, change.selection.getEventId(), change);
    },
    [MUTATION_TYPE.REMOVE_SELECTION_CHANGE](state, matchId: ReturnType<SelectionChange['selection']['getEventId']>) {
        Vue.delete(state.changes, matchId);
    },
    [MUTATION_TYPE.CLEAR_SELECTION_CHANGES](state) {
        Vue.set(state, 'changes', {});
    },
    [MUTATION_TYPE.CLEAR_ERRORS](state) {
        Vue.set(state, 'errors', []);
    },
    [MUTATION_TYPE.ADD_ERROR](state, error: IBetSlipError) {
        state.errors.push(error);
    },
    [MUTATION_TYPE.SET_SELECTION_ADD_ERROR](state, error: IBetSlipError | null) {
        state.selectionAddError = error;
    },
    [MUTATION_TYPE.SET_SELECTION_ERRORS](state, errors: BetSlipState['selectionErrors']) {
        Vue.set(state, 'selectionErrors', errors);
    },
    [MUTATION_TYPE.SET_PURCHASE_TYPE](state, type: BetSlipPurchaseType) {
        if (getConfig().countrySpecific.corona.areShopsDisabled) {
            return;
        }
        state.purchaseType = type;
    },
    [MUTATION_TYPE.SET_PAYOUT](state, payout: Payout) {
        state.payout = payout;
    },
    [MUTATION_TYPE.SET_PAYOUT_IS_LOCKED](state, isLocked: boolean) {
        state.payoutLocked = isLocked;
    },
    [MUTATION_TYPE.SET_WARNING](state, warning: IBetSlipError | null) {
        state.warning = warning;
    },
    [MUTATION_TYPE.SET_CHANGES](state, changes: BetSlipState['changes']) {
        Vue.set(state, 'changes', changes);
    },
    [MUTATION_TYPE.SET_BONUS_ELIGIBLITY_ERROR](state, error: IBetSlipError) {
        Vue.set(state, 'bonusError', error);
    },
    [MUTATION_TYPE.CLEAR_BONUS_ELIGIBLITY_ERROR](state) {
        Vue.set(state, 'bonusError', null);
    },
    [MUTATION_TYPE.SET_BONUS_WARNING](state, warning: IBetSlipError | null) {
        state.bonusWarning = warning;
    },
    [MUTATION_TYPE.SET_IS_BONUS_SELECTED](state, value: boolean) {
        state.isBonusSelected = value;
    },
    [MUTATION_TYPE.SET_IS_RECREATING_TICKET](state, value: boolean) {
        state.isRecreatingTicket = value;
    },
    [MUTATION_TYPE.SET_IS_SUBMITTING_ODD](state, { uuid, value }: { uuid: string, value: boolean }) {
        if (value) {
            Vue.set(state.isSubmittingOdd, uuid, true);
        } else {
            Vue.delete(state.isSubmittingOdd, uuid);
        }
    },
    [MUTATION_TYPE.SET_STAKE](state, stake: number) {
        state.stake = stake;
    },
    [MUTATION_TYPE.ADD_SELECTION](state, { selection, index }: { selection: ISelection; index?: number}) {
        if (index !== null && index !== undefined) {
            state.selections.splice(index, 0, selection);
        } else {
            state.selections.push(selection);
        }
    },
    [MUTATION_TYPE.UPDATE_SELECTION](state, { selection, index }: { selection: ISelection; index: number }) {
        state.selections[index].odd = selection.odd;
        state.selections[index].event = selection.event;
    },
    [MUTATION_TYPE.REMOVE_SELECTION](state, { index }: { index: number }) {
        state.selections.splice(index, 1);
    },
    [MUTATION_TYPE.CLEAR_SELECTIONS](state) {
        state.selections = [];
    },
    [MUTATION_TYPE.TOGGLE_SELECTION_IS_FIXED](state, selection: ISelection) {
        selection.toggleFixState(); // don't like this. Stands out from everything else
    },
    [MUTATION_TYPE.SET_TYPE](state, type: BetSlipType) {
        state.type = type;
    },
    [MUTATION_TYPE.ADD_SELECTED_SYSTEM](state, system: number) {
        state.selectedSystems.push(system);
    },
    [MUTATION_TYPE.REMOVE_SELECTED_SYSTEM](state, system) {
        const index = state.selectedSystems.findIndex((x) => x === system);
        state.selectedSystems.splice(index, 1);
    },
    [MUTATION_TYPE.CLEAR_SELECTED_SYSTEMS](state) {
        Vue.set(state, 'selectedSystems', []);
    },
    // only used for duplicate
    [MUTATION_TYPE.SET_SELECTIONS](state, selections: ISelection[]) {
        Vue.set(state, 'selections', selections);
    },
    [MUTATION_TYPE.SET_SELECTED_SYSTEMS](state, systems: number[]) {
        Vue.set(state, 'selectedSystems', systems);
    },
    [MUTATION_TYPE.SET_SELECTION_IS_FIXED](
        state,
        { selection, isFixed }: { selection: ISelection; isFixed: boolean }) {
        const selectionInState = state.selections.find((s) => s.isEqual(selection));
        if (selectionInState) {
            selectionInState.isFixed = isFixed;
        }
    },
    [MUTATION_TYPE.SET_BETSLIP_ERRORS](state, errors: BetSlipState['betSlipErrors']) {
        state.betSlipErrors = errors;
    },
    [MUTATION_TYPE.CLEAR_BETSLIP_ERRORS](state) {
        state.betSlipErrors.sportValidation = [];
    },
};
